import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/toaster';
import '@/index.css';
import router from '@/router';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import React from 'react';
import ReactDOM from 'react-dom/client';
import 'react-international-phone/style.css';
import { QueryClient, QueryClientProvider } from 'react-query';
import { RouterProvider } from 'react-router-dom';
import keycloak from './auth/keycloak';
import './locales';
import WebSocketProvider from './providers/websocket-provider';
import { OrganizationProvider } from './contexts/organization-context';
import { NotificationProvider } from './contexts/notification-context';
import { NotificationWebSocketProvider } from './providers/notification-websocket-provider';
import { DeploymentDetectionSetup } from './components/deployment-detection-setup';
import { ErrorBoundary } from './components/error-boundary';
import { logger } from '@/lib/logger';
import { initDeploymentDetection } from '@/utils/deployment-detector';
import { ReactQueryDevtools } from 'react-query/devtools';
import WebSocketDebuggerProvider from '@/providers/websocket-debugger-provider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on chunk loading errors - let deployment detector handle them
        if (error?.message?.includes('Failed to fetch dynamically imported module') ||
            error?.message?.includes('Loading chunk') ||
            error?.message?.includes('ChunkLoadError')) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
});

const eventLogger = (event: unknown, error: unknown) => {
  logger.debug('onKeycloakEvent', event, error);
};

const tokenLogger = (tokens: unknown) => {
  logger.debug('onKeycloakTokens', tokens);
};

// Initialize deployment detection
initDeploymentDetection();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ReactKeycloakProvider
    authClient={keycloak}
    initOptions={{
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      checkLoginIframe: false,
    }}
    onEvent={eventLogger}
    onTokens={tokenLogger}>
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            <OrganizationProvider>
              <NotificationWebSocketProvider>
                <NotificationProvider>
                  <WebSocketDebuggerProvider>
                    <WebSocketProvider>
                      <ErrorBoundary>
                        <DeploymentDetectionSetup />
                        <RouterProvider router={router} />
                        <Toaster />
                        <ReactQueryDevtools initialIsOpen={false} />
                      </ErrorBoundary>
                    </WebSocketProvider>
                  </WebSocketDebuggerProvider>
                </NotificationProvider>
              </NotificationWebSocketProvider>
            </OrganizationProvider>
          </ThemeProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </React.StrictMode>
  </ReactKeycloakProvider>,
);
